import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, logout as clearToken, getAccessToken, isImpersonating, setAccessToken, setImpersonationToken } from '@/api/client';

const AuthCtx = createContext(null);

/**
 * AuthProvider boots by:
 *   1. If localStorage has an access token → probe /api/auth/me with it.
 *   2. If localStorage is empty → attempt a silent /api/auth/refresh.
 *      A valid HttpOnly refresh cookie (issued at last login, 30-day TTL)
 *      will mint a new access token without bothering the user.
 *   3. If neither path succeeds → render the public surface, user must log in.
 */
export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);

  const refresh = useCallback(async () => {
    let tok = getAccessToken();

    // Path B: no token in localStorage → try one silent refresh.
    if (!tok) {
      try {
        const { data } = await api.post('/api/auth/refresh', {});
        if (data?.access_token) {
          setAccessToken(data.access_token);
          tok = data.access_token;
        }
      } catch {
        /* No refresh cookie or expired → continue to public surface. */
      }
    }

    if (!tok) {
      setAuthUser(null);
      setBootLoading(false);
      return null;
    }
    try {
      const { data } = await api.get('/api/auth/me');
      setAuthUser(data);
      return data;
    } catch {
      clearToken();
      setAuthUser(null);
      return null;
    } finally {
      setBootLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (identifier, password, captchaToken) => {
    const body = { identifier, password };
    if (captchaToken) body.captcha_token = captchaToken;
    const { data } = await api.post('/api/auth/login', body);
    setAccessToken(data.access_token);
    setAuthUser(data.user);
    return data;
  }, []);

  const signup = useCallback(async (payload) => {
    const { data } = await api.post('/api/auth/signup', payload);
    setAccessToken(data.access_token);
    setAuthUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    // An impersonation tab holds only its own 60-minute token; the refresh cookie in the browser is
    // the god admin's. Logging out there must drop the impersonation, not end the admin's session.
    if (isImpersonating()) {
      setImpersonationToken(null);
      setAuthUser(null);
      return;
    }
    try {
      await api.post('/api/auth/logout');
    } catch {
      /* stateless server-side — ignore */
    }
    clearToken();
    setAuthUser(null);
  }, []);

  return <AuthCtx.Provider value={{ authUser, bootLoading, login, signup, logout, refresh, isAdmin: !!authUser?.is_admin }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
